export const IDLE_STORAGE_KEY = 'trellis.auth.lastActivityAt'

export type StoredActivity =
  | { readonly kind: 'absent' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'stamped'; readonly at: number }

const ABSENT: StoredActivity = Object.freeze({ kind: 'absent' })
const INVALID: StoredActivity = Object.freeze({ kind: 'invalid' })

let inMemoryStamp: number | null = null
let storageIsWritable = true

function storage(): Storage | null {
  try {
    return globalThis.localStorage
  } catch {
    return null
  }
}

export function readLastActivity(): StoredActivity {
  const store = storage()

  if (!store || !storageIsWritable) {
    return inMemoryStamp === null ? ABSENT : { kind: 'stamped', at: inMemoryStamp }
  }

  let raw: string | null
  try {
    raw = store.getItem(IDLE_STORAGE_KEY)
  } catch {
    return inMemoryStamp === null ? ABSENT : { kind: 'stamped', at: inMemoryStamp }
  }

  if (raw === null) return ABSENT

  const parsed = Number(raw)
  if (raw.trim() === '' || !Number.isFinite(parsed)) return INVALID

  return { kind: 'stamped', at: parsed }
}

export function writeLastActivity(at: number): void {
  inMemoryStamp = at
  const store = storage()
  if (!store) return

  try {
    store.setItem(IDLE_STORAGE_KEY, String(at))
    storageIsWritable = true
  } catch {
    storageIsWritable = false
  }
}

export function clearLastActivity(): void {
  inMemoryStamp = null
  try {
    storage()?.removeItem(IDLE_STORAGE_KEY)
  } catch {}
}

export function subscribeToLastActivity(onChange: () => void): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key === null || event.key === IDLE_STORAGE_KEY) onChange()
  }

  globalThis.addEventListener('storage', handler)
  return () => {
    globalThis.removeEventListener('storage', handler)
  }
}

export function resetIdleStoreForTests(): void {
  inMemoryStamp = null
  storageIsWritable = true
}
